import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Dialog, IconButton } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { FMSThemeContext } from "contexts/FMSThemeContext";
import { MaterialContext } from "contexts/MaterialsContext";
import {
  getSupplierOptions,
  confirmConsolidation,
  processConsolidation,
} from "api/CateringApi";
import {
  ICreatedPurchaseOrder,
  IMaterialSupplierOption,
  IProposedPurchaseOrder,
} from "types/CateringEvent";
import { IMaterial } from "types/Material";
import { MaterialClassification } from "types/MaterialClassification";
import { displayQuantity } from "types/MeasurementUnit";
import styles from "./CateringPOPreview.module.css";

interface Props {
  eventIds: string[];
  onClose: () => void;
  onSuccess: (message: string) => void;
}

type Step = "assign" | "confirm" | "process" | "success";

const CateringPOPreview: React.FC<Props> = ({ eventIds, onClose, onSuccess }) => {
  const { primaryColor, primaryTextColor } = useContext(FMSThemeContext);
  const { branchWiseMaterialsMap } = useContext(MaterialContext);
  const eventIdsRef = useRef(eventIds);

  // Build a flat materialId -> IMaterial lookup for classification grouping
  const allMaterialsMap = new Map<string, IMaterial>();
  for (const ctx of branchWiseMaterialsMap.values()) {
    for (const [id, mat] of ctx.allMaterialsMap) {
      if (!allMaterialsMap.has(id)) allMaterialsMap.set(id, mat);
    }
  }

  const [loading, setLoading] = useState(true);
  const [materialOptions, setMaterialOptions] = useState<IMaterialSupplierOption[]>([]);
  const [supplierAssignment, setSupplierAssignment] = useState<Record<string, string>>({});
  const [step, setStep] = useState<Step>("assign");
  const [proposedPOs, setProposedPOs] = useState<IProposedPurchaseOrder[]>([]);
  const [createdPOs, setCreatedPOs] = useState<ICreatedPurchaseOrder[]>([]);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const data = await getSupplierOptions({ cateringEventIds: eventIdsRef.current });
        if (data) {
          setMaterialOptions(data.materialSupplierOptions);
          const autoAssign: Record<string, string> = {};
          for (const mat of data.materialSupplierOptions) {
            if (mat.availableSuppliers.length >= 1) {
              autoAssign[mat.materialId] = mat.availableSuppliers[0].supplierId;
            }
          }
          setSupplierAssignment(autoAssign);
        }
      } catch (err) {
        console.error("Supplier options error:", err);
      }
      setLoading(false);
    };
    fetch();
  }, []);

  const handleSupplierChange = (materialId: string, supplierId: string) => {
    setSupplierAssignment((prev) => ({ ...prev, [materialId]: supplierId }));
    setError("");
  };

  const handleProceedToConfirm = async () => {
    const unassigned = materialOptions.filter((m) => !supplierAssignment[m.materialId]);
    if (unassigned.length > 0) {
      setError(" "); // trigger red highlight on dropdowns without showing a message
      return;
    }
    setError("");
    setWorking(true);
    try {
      const data = await confirmConsolidation({
        cateringEventIds: eventIdsRef.current,
        materialIdToSupplierId: supplierAssignment,
      });
      if (data) {
        setProposedPOs(data.proposedPurchaseOrders);
        setStep("confirm");
      } else {
        setError("Failed to preview purchase orders");
      }
    } catch {
      setError("Failed to preview purchase orders");
    } finally {
      setWorking(false);
    }
  };

  const handleProcess = async () => {
    setWorking(true);
    try {
      const data = await processConsolidation({
        cateringEventIds: eventIdsRef.current,
        materialIdToSupplierId: supplierAssignment,
      });
      if (data) {
        setCreatedPOs(data.createdPurchaseOrders);
        setStep("success");
      } else {
        setError("Failed to generate purchase orders");
        setStep("confirm");
      }
    } catch {
      setError("Failed to generate purchase orders");
      setStep("confirm");
    } finally {
      setWorking(false);
    }
  };

  const handleClose = () => {
    if (step === "success") {
      onSuccess(`Generated ${createdPOs.length} purchase order(s)`);
    } else {
      onClose();
    }
  };

  return (
    <Dialog open={true} onClose={(_, reason) => { if (reason !== "backdropClick") handleClose(); }} maxWidth="md">
      <div style={{ position: "absolute", top: 8, right: 8 }}>
        <IconButton onClick={handleClose} size="small">
          <CloseRoundedIcon />
        </IconButton>
      </div>
      <div className={styles.dialogContent}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 40 }}>Loading supplier options...</div>
        ) : materialOptions.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: "#d32f2f" }}>
            No materials found. Ensure all selected events have menu items configured.
          </div>
        ) : step === "assign" ? (
          <>
            <div className={styles.title}>Raise Purchase Orders</div>

            <div className={styles.sectionTitle}>Aggregated Raw Materials</div>
            {(() => {
              const groups = new Map<string, IMaterialSupplierOption[]>();
              for (const mat of materialOptions) {
                const m = allMaterialsMap.get(mat.materialId);
                const category = m?.classification ?? "Other";
                const list = groups.get(category) ?? [];
                list.push(mat);
                groups.set(category, list);
              }
              const classOrder = Object.values(MaterialClassification);
              const sorted = [...groups.entries()].sort((a, b) => {
                const ai = classOrder.indexOf(a[0] as MaterialClassification);
                const bi = classOrder.indexOf(b[0] as MaterialClassification);
                return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
              });
              return sorted.map(([category, mats]) => (
                <div key={category} className={styles.categoryGroup}>
                  <div className={styles.categoryGroupLabel}>{category}</div>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Material</th>
                        <th>Total Qty</th>
                        <th>Supplier</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mats.map((mat) => (
                        <tr key={mat.materialId}>
                          <td>{mat.materialName}</td>
                          <td>{displayQuantity(mat.displayQuantity)}</td>
                          <td>
                            <select
                              className={`${styles.supplierSelect} ${
                                error && !supplierAssignment[mat.materialId] ? styles.supplierSelectError : ""
                              }`}
                              value={supplierAssignment[mat.materialId] ?? ""}
                              onChange={(e) => handleSupplierChange(mat.materialId, e.target.value)}
                            >
                              <option value="">Select supplier</option>
                              {mat.availableSuppliers.map((s) => (
                                <option key={s.supplierId} value={s.supplierId}>
                                  {s.supplierName}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ));
            })()}

            <div className={styles.buttonRow}>
              <Button variant="outlined" onClick={onClose} disabled={working}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleProceedToConfirm}
                disabled={working}
                style={{ backgroundColor: primaryColor, color: primaryTextColor }}
              >
                {working ? "Loading..." : "Preview POs"}
              </Button>
            </div>
          </>
        ) : step === "confirm" ? (
          <>
            <div className={styles.title}>Confirm Purchase Orders</div>

            {proposedPOs.map((po) => (
              <div key={po.supplierId + po.branchId} className={styles.supplierGroup}>
                <div className={styles.supplierGroupTitle}>
                  {po.supplierName}
                  <span style={{ fontWeight: 400, fontSize: 12, color: "#666", marginLeft: 8 }}>
                    ({po.branchName})
                  </span>
                </div>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Material</th>
                      <th>Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {po.materials.map((m) => (
                      <tr key={m.materialId}>
                        <td>{m.materialName}</td>
                        <td>{displayQuantity(m.displayQuantity)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}

            {error && <div className={styles.errorText}>{error}</div>}

            <div className={styles.buttonRow}>
              <Button variant="outlined" onClick={() => setStep("assign")} disabled={working}>
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleProcess}
                disabled={working}
                style={{ backgroundColor: "#2e7d32", color: "#fff" }}
              >
                {working ? "Generating..." : "Confirm & Generate POs"}
              </Button>
            </div>
          </>
        ) : step === "success" ? (
          <SuccessView
            poIds={createdPOs}
            onDone={() => onSuccess(`Generated ${createdPOs.length} purchase order(s)`)}
          />
        ) : null}
      </div>
    </Dialog>
  );
};

const SuccessView: React.FC<{
  poIds: ICreatedPurchaseOrder[];
  onDone: () => void;
}> = ({ poIds, onDone }) => {
  const history = useHistory();
  const { primaryColor, primaryTextColor } = useContext(FMSThemeContext);

  return (
    <div className={styles.successSection}>
      <div className={styles.successTitle}>Purchase Orders Generated Successfully!</div>
      <div className={styles.sectionTitle} style={{ textAlign: "left" }}>
        Generated {poIds.length} Purchase Order(s)
      </div>
      {poIds.map((po) => (
        <div key={po.id} className={styles.poSummaryRow}>
          <span>{po.vanityNumber}</span>
          <span
            className={styles.poLink}
            onClick={() => history.push(`/purchase-orders/${po.id}`)}
          >
            View PO
          </span>
        </div>
      ))}
      <div className={styles.buttonRow}>
        <Button
          variant="contained"
          onClick={onDone}
          style={{ backgroundColor: primaryColor, color: primaryTextColor }}
        >
          Done
        </Button>
      </div>
    </div>
  );
};

export default CateringPOPreview;
